/**
 * Shared domain types + Zod schemas - the contract seam between the backend,
 * the platform SPA, and the CDK infra. Intentionally minimal for now: the
 * newsletter model grows when the onboarding wizard is reworked.
 */
import { z } from 'zod';

export type TenantId = string;
export type NewsletterId = string;
export type UserId = string;

export const CADENCES = ['weekly', 'biweekly', 'monthly'] as const;
export type Cadence = (typeof CADENCES)[number];

// --- Tenant -----------------------------------------------------------------

export const TENANT_STATUSES = ['active', 'suspended'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export interface Tenant {
  readonly tenantId: TenantId;
  readonly ownerUserId: UserId;
  readonly plan: string;
  readonly status: TenantStatus;
  readonly createdAt: string;
}

// --- Newsletter -------------------------------------------------------------
// A tenant owns many newsletters. Kept minimal until the wizard rework defines
// brand / sections / sources.

// Lifecycle: a newsletter starts as a `draft`, becomes `scheduled` once the owner
// picks a cadence and a first-issue date (see `nextIssueAt`), and later `active`
// when real automated delivery begins (a LATER phase). `scheduled` deliberately
// does NOT mean anything is being sent yet; it means a first issue is on the
// calendar and we will confirm before the first real send.
export const NEWSLETTER_STATUSES = ['draft', 'scheduled', 'active'] as const;
export type NewsletterStatus = (typeof NEWSLETTER_STATUSES)[number];

// The provisioning state of a newsletter's OWN SES sending identity on its
// subdomain (`no-reply@<subdomain>.nuffsaid.cloud`). Server-managed (NOT part of
// the user-editable config): `none` = never enabled (mail falls back to the
// platform identity); `provisioning` = the SES identity + DNS were created and are
// verifying; `verified` = SES reports it verified for sending, so the send path
// upgrades the From to the subdomain identity. Additive + optional so pre-existing
// rows (absent field) simply read as `none`.
export const SENDING_IDENTITY_STATES = ['none', 'provisioning', 'verified'] as const;
export type SendingIdentityState = (typeof SENDING_IDENTITY_STATES)[number];

// --- Newsletter config ------------------------------------------------------
// Everything the onboarding wizard gathers, distilled into serialisable fields
// the discovery planner can read. All optional so older rows stay valid; reads
// default them the way `topics` defaults to `[]`.

/** Why the newsletter exists: a company, a community, or free-text "other". */
export const NEWSLETTER_PURPOSE_KINDS = ['company', 'community', 'other'] as const;
export type NewsletterPurposeKind = (typeof NEWSLETTER_PURPOSE_KINDS)[number];

/** The two-way audience split derived from purpose; steers tone + discovery. */
export const AUDIENCES = ['company', 'community'] as const;
export type Audience = (typeof AUDIENCES)[number];

/** Zod schema for the newsletter purpose (a kind plus optional free text). */
export const newsletterPurposeSchema = z.object({
  kind: z.enum(NEWSLETTER_PURPOSE_KINDS),
  text: z.string().trim().max(2000).optional(),
});

/**
 * Zod schema for the newsletter voice: a named preset id, or a pasted custom
 * sample. The custom sample is persisted but is NEVER sent to the discovery
 * planner (it does not help search); only presets/audience shape tone downstream.
 */
export const newsletterVoiceSchema = z.union([
  z.object({ kind: z.literal('preset'), id: z.string().trim().min(1).max(60) }),
  z.object({ kind: z.literal('custom'), text: z.string().trim().min(1).max(2000) }),
]);

/** Zod schema for one liked-newsletter reference. */
export const likedNewsletterSchema = z.object({
  label: z.string().trim().min(1).max(200),
  url: z.string().trim().min(1).max(500),
});

/**
 * Social platforms the owner can attach a handle for. Deliberately open-ended:
 * `other` (plus `website`) means any link is representable, and NEW platforms are
 * a one-line addition to this list. NEVER user-facing jargon; the app maps each
 * to a friendly label.
 */
export const SOCIAL_PLATFORMS = [
  'x',
  'instagram',
  'linkedin',
  'youtube',
  'tiktok',
  'website',
  'other',
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/** Zod schema for one social link: a platform plus a handle or URL for it. */
export const socialSchema = z.object({
  platform: z.enum(SOCIAL_PLATFORMS),
  handle: z.string().trim().min(1).max(200),
});

/** The wizard's purpose step: a kind plus optional free text for "other". */
export type NewsletterPurpose = z.infer<typeof newsletterPurposeSchema>;

/** The chosen writing voice: a named preset id, or a pasted custom sample. */
export type NewsletterVoice = z.infer<typeof newsletterVoiceSchema>;

/** A publication the owner likes: the planner samples it to find similar content. */
export type LikedNewsletter = z.infer<typeof likedNewsletterSchema>;

/** One of the newsletter author's social links (platform + handle/URL). */
export type Social = z.infer<typeof socialSchema>;

/**
 * THE SOURCE OF TRUTH for a newsletter's editable configuration.
 *
 * Every field the product gathers about a newsletter lives here exactly once.
 * Both {@link newsletterConfigSchema} (post-create PATCH) and
 * {@link newsletterInputSchema} (create) are derived from this shape, and the
 * stored {@link Newsletter} interface mirrors it, so the schema, the API
 * contract, and persistence can never drift apart.
 *
 * TO ADD A FIELD later, it is a small, mechanical change in ONE ordered path:
 *   1. Add the field here (and to the {@link Newsletter} interface below). All
 *      new fields MUST be `.optional()` so pre-existing rows stay valid.
 *   2. Default it on read in @nuff/data-access `toNewsletter`, and carry it in
 *      `create` (data-access create/read defaults).
 *   3. Show + edit it in apps/platform `NewsletterView` (display/edit), and, when
 *      it originates in the wizard, carry it in `draftToNewsletterInput`.
 * updateConfig() is generic over the config keys, so no data-access write change
 * is needed for a new field. Because every field is optional and defaulted on
 * read, OLD newsletters simply have the new fields empty rather than breaking.
 */
const newsletterConfigShape = {
  // --- Delivery ---
  /** How often issues go out; also settable in the config editor, not just at create. */
  cadence: z.enum(CADENCES).optional(),
  // --- Content ---
  /** Focus topics; what source discovery searches on. A single word OR a paragraph. */
  topics: z.array(z.string().trim().min(1).max(2000)).max(10).optional(),
  /** The chosen writing voice (preset id or custom sample). */
  voice: newsletterVoiceSchema.optional(),
  /**
   * A SHORT tone descriptor (e.g. "casual and conversational"), distinct from the
   * `voice` sample: `voice.custom.text` is a PASTED writing sample, whereas this is
   * a described tone the refine chat sets. Kept separate so setting a tone can never
   * clobber a pasted sample. Fed into the preview intro's tone.
   */
  voiceNote: z.string().trim().min(1).max(400).optional(),
  /** Publications the owner likes; the planner interprets them to find similar content. */
  likedNewsletters: z.array(likedNewsletterSchema).max(10).optional(),
  // --- Identity ---
  /** A short one-line description shown under the name. */
  tagline: z.string().trim().min(1).max(200).optional(),
  /** Why the newsletter exists (wizard purpose step). */
  purpose: newsletterPurposeSchema.optional(),
  /** Company vs community, derived from purpose and persisted for the planner. */
  audience: z.enum(AUDIENCES).optional(),
  // --- Author & brand ---
  /** The author/brand name shown as the byline. */
  authorName: z.string().trim().min(1).max(120).optional(),
  /** A short author/brand bio. */
  authorBio: z.string().trim().min(1).max(2000).optional(),
  /** The author's social links (extensible list of platform + handle). */
  socials: z.array(socialSchema).max(20).optional(),
  /**
   * The human-readable FROM NAME readers see in their inbox (e.g. "The Product AI
   * Brief"). It becomes the From header's DISPLAY NAME only; the sending ADDRESS is
   * always `no-reply@<subdomain>.<platformDomain>`. Defaults to the newsletter's own
   * `name` when unset, so out-of-the-box mail already shows a real name rather than a
   * bare no-reply. Bounded + non-empty; the send layer strips CR/LF and safe-quotes it
   * so a value can never inject an email header.
   */
  fromName: z.string().trim().min(1).max(120).optional(),
  /** Where replies to an issue should go (the From's Reply-To). Omitted -> no Reply-To. */
  replyEmail: z.string().trim().email().max(200).optional(),
  // --- Locale ---
  /** An optional place, for "local" topics and nearby coverage. */
  location: z.string().trim().min(1).max(120).optional(),
  /** IANA timezone (e.g. "America/Chicago"); when issues send. Auto-captured on claim. */
  timezone: z.string().trim().min(1).max(120).optional(),
} as const;

export interface Newsletter {
  readonly tenantId: TenantId;
  readonly newsletterId: NewsletterId;
  readonly name: string;
  readonly subdomain: string | null;
  readonly cadence: Cadence | null;
  /**
   * Focus topics captured in the wizard; what source discovery searches on. An
   * entry may be a single word OR a whole paragraph the planner expands.
   */
  readonly topics: readonly string[];
  /** A short one-line description shown under the name. Optional for older rows. */
  readonly tagline?: string;
  /** Why the newsletter exists (wizard purpose step). Optional for older rows. */
  readonly purpose?: NewsletterPurpose;
  /** Company vs community, derived from purpose and persisted for the planner. */
  readonly audience?: Audience;
  /** The chosen writing voice (preset id or custom sample). */
  readonly voice?: NewsletterVoice;
  /**
   * A short tone descriptor set from the refine chat (e.g. "casual and
   * conversational"), separate from the pasted `voice` sample so one never
   * clobbers the other. Optional for older rows.
   */
  readonly voiceNote?: string;
  /** Publications the owner likes; the planner interprets them to find similar content. */
  readonly likedNewsletters?: readonly LikedNewsletter[];
  /** The author/brand name shown as the byline. */
  readonly authorName?: string;
  /** A short author/brand bio. */
  readonly authorBio?: string;
  /** The author's social links (extensible list of platform + handle). */
  readonly socials?: readonly Social[];
  /**
   * The human-readable FROM NAME shown in the reader's inbox; becomes the From
   * header display name. Defaults to the newsletter `name` when unset. Optional for
   * older rows.
   */
  readonly fromName?: string;
  /** Where replies to an issue should go (the From's Reply-To). Optional for older rows. */
  readonly replyEmail?: string;
  /** An optional place, for "local" topics. */
  readonly location?: string;
  /** IANA timezone (e.g. "America/Chicago"); auto-captured from the browser on claim. */
  readonly timezone?: string;
  readonly status: NewsletterStatus;
  /**
   * The provisioning state of this newsletter's OWN SES sending identity on its
   * subdomain. Absent on rows that never enabled it (read as `none`); set to
   * `provisioning` when the identity + DNS are created and `verified` once SES
   * verifies it, at which point outbound mail is sent from
   * `no-reply@<subdomain>.nuffsaid.cloud` instead of the platform fallback.
   */
  readonly sendingIdentity?: SendingIdentityState;
  /**
   * When the first issue is scheduled for (ISO 8601). Set alongside `cadence`
   * when the owner schedules the newsletter (status -> `scheduled`); absent on a
   * draft. Real automated delivery is a later phase, so this is a target date we
   * confirm before ever sending, not a promise that a send happened.
   */
  readonly nextIssueAt?: string;
  /**
   * How many CONFIRMED (real, opted-in) subscribers this newsletter has. A
   * READ-TIME enrichment the list endpoints (`GET /me`, `GET /newsletters`) attach
   * so the dashboard can surface a real recipient count per card; it is NOT part of
   * the persisted config and is absent on rows read outside those list paths (read
   * as 0). Kept optional so nothing that builds a bare Newsletter has to supply it.
   */
  readonly confirmedSubscribers?: number;
  /**
   * How many issues have been SENT to subscribers. Incremented once per completed
   * send-to-subscribers (never per-recipient, never on a send-test), so the NEXT
   * issue to publish is `issuesSent + 1`. Persisted; absent on rows that never sent
   * (read as 0). This is the source of the issue number handed to the content agents,
   * so a regenerated draft keeps the same number while an actual send advances it.
   */
  readonly issuesSent?: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Payload accepted when creating a newsletter (from the dashboard or wizard).
 * Every config field beyond `name` is optional so a bare `{ name }` still
 * validates. Derived from the {@link newsletterConfigShape} source of truth
 * (which carries `cadence`) plus the create-only identity field (subdomain).
 */
export const newsletterInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  subdomain: z.string().trim().min(3).max(40).optional(),
  ...newsletterConfigShape,
});
export type NewsletterInput = z.infer<typeof newsletterInputSchema>;

/**
 * The config subset that can be edited after creation (all optional). This is
 * the schema the PATCH /newsletters/{id} endpoint validates against, so it must
 * stay the full source-of-truth shape or new fields would be silently dropped.
 */
export const newsletterConfigSchema = z.object(newsletterConfigShape);
export type NewsletterConfig = z.infer<typeof newsletterConfigSchema>;

// --- Sources & sections -----------------------------------------------------
// Source discovery turns a newsletter's topics into suggested sections. Each
// section carries a plain-English `summary` describing what the reader gets, and
// binds one or more discovered sources. A source is one coherent content stream,
// modelled as a discriminated union on `sourceType`:
//   - a QUERY-STREAM: a provider search (Google News, Hacker News, ...) where the
//     channel + query IS the source, and honestly says so ("Hacker News: ...").
//   - a SITE-FEED: a real publication represented as ITSELF, named after the site,
//     never after the channel we happened to discover it through.
// The internal `Treatment` enum still routes the render pipeline but is NEVER a
// user-facing label.

/** How a source's items become section content, from pure code to agentic. Internal only. */
export const TREATMENTS = ['list', 'data', 'digest', 'spin', 'commentary'] as const;
export type Treatment = (typeof TREATMENTS)[number];

export const SOURCE_KINDS = ['news', 'forum', 'video', 'podcast', 'web', 'data'] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

/** The two shapes a discovered source can take. */
export const SOURCE_TYPES = ['query-stream', 'site-feed'] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

/**
 * WHERE discovery pulls items from. Internal telemetry only: a channel is NEVER
 * a user-facing label. A site found via Google News is still named after itself.
 */
export const CHANNELS = ['googleNews', 'hackerNews', 'reddit', 'youtube', 'rss'] as const;
export type Channel = (typeof CHANNELS)[number];

/** Plain-English channel names, used ONLY when the channel legitimately IS the source. */
export const CHANNEL_DISPLAY_NAMES: Record<Channel, string> = {
  googleNews: 'Google News',
  hackerNews: 'Hacker News',
  reddit: 'Reddit',
  youtube: 'YouTube',
  rss: 'Web',
};

/** How fresh, how often, and how much a source produces (from evaluation). */
export interface SourceMetrics {
  readonly freshnessHours: number | null;
  readonly cadencePerWeek: number;
  readonly volume: number;
}

/** Fields shared by every persisted source reference. */
interface SourceRefBase {
  readonly kind: SourceKind;
  /** The topic run that surfaced this source. Internal/telemetry, not a label. */
  readonly query: string;
  readonly metrics: SourceMetrics;
  /**
   * True when a broad topic surfaced a hyper-narrow source (e.g. a single-team
   * blog for "basketball"). Ranking uses it to keep niche sources out of the
   * default set. Optional + serialisable; a seam for later breadth work.
   */
  readonly overNarrow?: boolean;
}

/**
 * (a) A provider search stream: the channel + query IS the source. Legitimately
 * provider-shaped, so it honestly names the provider ("Google News: ...").
 */
export interface QueryStreamSourceRef extends SourceRefBase {
  readonly sourceType: 'query-stream';
  readonly channel: Channel;
  /** The searched text, e.g. "basketball news". */
  readonly queryText: string;
  /** The live page a reader could open to see the stream. */
  readonly streamUrl: string;
}

/**
 * (b) A real publication represented as ITSELF, named after the site. The
 * channel we found it through is demoted to `discoveredVia` (telemetry only).
 */
export interface SiteFeedSourceRef extends SourceRefBase {
  readonly sourceType: 'site-feed';
  /** The site's own name, e.g. "ESPN", "Arkansas Razorbacks Updates". */
  readonly siteName: string;
  /** The site homepage / section URL. */
  readonly siteUrl: string;
  /** Resolved RSS/Atom feed, or null until @nuff/extract resolves it (later phase). */
  readonly feedUrl: string | null;
  /** How we found it. Telemetry ONLY, never a user label. */
  readonly discoveredVia: Channel;
}

/** A discovered source bound to a suggested section. */
export type SourceRef = QueryStreamSourceRef | SiteFeedSourceRef;

/** The human name for a source: the site's own name, or "Google News: <query>". */
export function sourceDisplayName(ref: SourceRef): string {
  return ref.sourceType === 'site-feed'
    ? ref.siteName
    : `${CHANNEL_DISPLAY_NAMES[ref.channel]}: ${ref.queryText}`;
}

/** The URL a reader can open for a source, if any. */
export function sourceUrl(ref: SourceRef): string | null {
  return ref.sourceType === 'site-feed' ? ref.siteUrl : ref.streamUrl;
}

export const SECTION_STATUSES = ['suggested', 'selected', 'dismissed'] as const;
export type SectionStatus = (typeof SECTION_STATUSES)[number];

/** A suggested section produced by discovery, persisted per newsletter. */
export interface SuggestedSection {
  readonly tenantId: TenantId;
  readonly newsletterId: NewsletterId;
  readonly sectionId: string;
  readonly title: string;
  /** One plain-English sentence describing what the reader gets. The user label. */
  readonly summary: string;
  /** Internal render-pipeline routing. NEVER shown to users. */
  readonly treatment: Treatment;
  readonly why: string;
  readonly sources: readonly SourceRef[];
  readonly metrics: SourceMetrics;
  readonly defaultSelected: boolean;
  readonly status: SectionStatus;
  /**
   * The section's position in the curated running order (0-based), when the owner
   * (or "Propose structure") has arranged the newsletter. Absent on freshly
   * discovered sections; reads sort selected sections by it, unset last.
   */
  readonly order?: number;
  /**
   * The owner's editable AI INSTRUCTION for this section: a plain-language steer for
   * how our AI writes it (voice, emphasis, framing). Absent until the owner types
   * one, in which case the writer behaves exactly as before. It STEERS the prose; it
   * never overrides the no-fabrication guardrails, so it can shape but never invent.
   */
  readonly instruction?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Body accepted by PATCH /newsletters/{id}/sections/{sectionId}: the owner's editable
 * AI instruction for one section. An empty string is allowed on purpose, so the owner
 * can CLEAR a previously typed instruction and return the section to default writing.
 */
export const sectionInstructionSchema = z.object({
  instruction: z.string().trim().max(2000),
});
export type SectionInstructionInput = z.infer<typeof sectionInstructionSchema>;

/**
 * Body accepted by PATCH /newsletters/{id}/sections/{sectionId}: the owner's editable
 * per-section levers, ALL optional so one PATCH can carry either an `instruction`
 * (the plain-language writing steer; `''` CLEARS it) or a new `treatment` (the honest
 * "how much AI touches the source" knob), or both. Refined to require at least one, so
 * an empty body is a 400 rather than a silent no-op. Both changes only take effect on
 * the next (per-section or full) regenerate; neither ever fabricates content.
 */
export const sectionEditSchema = z
  .object({
    instruction: z.string().trim().max(2000).optional(),
    treatment: z.enum(TREATMENTS).optional(),
  })
  .refine((value) => value.instruction !== undefined || value.treatment !== undefined, {
    message: 'Provide an instruction or a treatment.',
  });
export type SectionEditInput = z.infer<typeof sectionEditSchema>;

/**
 * A compact, STYLE-ONLY characterization of how a publication the owner loves is
 * written, distilled from a sample of its recent headlines/lead paragraphs. It is
 * a property of the PUBLICATION (not the tenant), so it is cached globally by URL
 * and shared across tenants: it holds NO user content, only public-site voice
 * signal. E2.1 derives a per-section starter instruction from it. Every field is
 * short and bounded; the characterizer sanitizes into this shape (empty strings /
 * arrays where the sample gave nothing) and NEVER invents from a bare name.
 */
export const likedBlueprintSchema = z.object({
  /** Overall voice, e.g. "wry, insider, plain-spoken". */
  tone: z.string().trim().max(120),
  /** Sentence rhythm / length pattern. */
  cadence: z.string().trim().max(120),
  /** The kinds of section it runs, e.g. "quick hits", "one deep read". */
  sectionTypes: z.array(z.string().trim().min(1).max(40)).max(6),
  /** How it treats and links its sources. */
  sourceHandling: z.string().trim().max(160),
  /** Recurring signature moves, e.g. "opens with a number". */
  signatureMoves: z.array(z.string().trim().min(1).max(80)).max(4),
  /** Things it conspicuously avoids, e.g. "hypey adjectives". */
  avoid: z.array(z.string().trim().min(1).max(80)).max(4),
});
export type LikedBlueprint = z.infer<typeof likedBlueprintSchema>;

/**
 * The outcome of characterizing ONE liked newsletter: either a usable
 * {@link LikedBlueprint}, or an honest `insufficient` with a plain reason (the URL
 * was unfetchable / name-only, or the sample was too thin to characterize). The
 * NEGATIVE is cached too, so a dead URL is not re-fetched across tenants. We NEVER
 * fabricate a blueprint from nothing: no real sample means `insufficient`.
 */
export type LikedBlueprintResult =
  | { readonly insufficient: true; readonly reason: string }
  | { readonly insufficient: false; readonly blueprint: LikedBlueprint };

/**
 * One seeded starter instruction returned by POST
 * /newsletters/{id}/sections/seed-instructions: a SHORT, style-only steer derived
 * from a liked newsletter's blueprint, for the owner to review (keep / edit /
 * dismiss). `sourceLabel` attributes it honestly to the publication it came from.
 */
export interface SeededInstruction {
  readonly sectionId: string;
  readonly instruction: string;
  readonly sourceLabel: string;
}

/**
 * One liked newsletter we could NOT seed from, named honestly so the UI can say
 * "we couldn't read 1 of your 4" rather than imply we studied something we didn't.
 */
export interface MissedLikedNewsletter {
  readonly label: string;
  readonly reason: string;
}

/** The response body of POST /newsletters/{id}/sections/seed-instructions. */
export interface SeedInstructionsResult {
  readonly seeds: readonly SeededInstruction[];
  readonly missed: readonly MissedLikedNewsletter[];
}

/**
 * One selection update the owner makes to a discovered section: keep it in the
 * newsletter (`selected`), leave it available (`suggested`), or hide it
 * (`dismissed`), optionally with an explicit running-order position. Sent in a
 * batch to PATCH /newsletters/{id}/sections.
 */
export const sectionSelectionSchema = z.object({
  sectionId: z.string().trim().min(1).max(120),
  status: z.enum(SECTION_STATUSES),
  order: z.number().int().min(0).max(1000).optional(),
});
export type SectionSelection = z.infer<typeof sectionSelectionSchema>;

/** Body accepted by PATCH /newsletters/{id}/sections: a bounded batch of updates. */
export const sectionSelectionsSchema = z.object({
  updates: z.array(sectionSelectionSchema).min(1).max(50),
});
export type SectionSelectionsInput = z.infer<typeof sectionSelectionsSchema>;

/** What `GET /me` returns: the signed-in user's tenant + their newsletters. */
export interface MeResponse {
  readonly tenant: Tenant;
  readonly newsletters: readonly Newsletter[];
}

// --- First-issue preview ----------------------------------------------------
// A NON-persisted assembly of what a newsletter's first issue could look like:
// a subject, a short intro written in the newsletter's voice, and one block per
// default-selected section (its title + plain summary + a few real, current
// headlines). Returned by POST /newsletters/{id}/preview so the owner can see
// value before scheduling. Never stored: it is a fresh look each time.

/** One sampled headline in a preview: a title and the URL a reader could open. */
export interface IssuePreviewItem {
  readonly title: string;
  readonly url: string;
}

/** One section of a preview issue: its heading, plain summary, and sample items. */
export interface IssuePreviewSection {
  readonly title: string;
  readonly summary: string;
  /** A few real, current headlines for the section; may be empty if fetching failed. */
  readonly items: readonly IssuePreviewItem[];
}

/** The assembled preview of a newsletter's first issue. */
export interface IssuePreview {
  readonly subject: string;
  readonly intro: string;
  readonly sections: readonly IssuePreviewSection[];
}

/** Body accepted by POST /newsletters/{id}/schedule: just the desired cadence. */
export const newsletterScheduleSchema = z.object({
  cadence: z.enum(CADENCES),
});
export type NewsletterScheduleInput = z.infer<typeof newsletterScheduleSchema>;

// --- Issue content model ----------------------------------------------------
// The PERSISTED first issue: prose-first, one draft per newsletter. Unlike the
// ephemeral {@link IssuePreview} (a fresh look each request), an Issue is stored,
// re-generatable, and editable. Each block carries a markdown-ish `body` with
// inline `[text](url)` links; a link-roundup is ONE block with role `links`, never
// the whole issue. In P1 the body is seeded from the section summary (the `body`
// default of `''` is the seam a P2 Sonnet prose writer swaps into); `generatedBy`
// records what wrote it ('assembly' in P1, a model id in P2).

/**
 * The role a content block plays in the issue: an opening `intro`, a prose
 * `story` (a short take), a deeper `commentary` (a fuller analytical read), a
 * `digest` of a discussion, a `links` roundup, or a closing `signoff`. `story` and
 * `commentary` render identically (flowing prose) but are DISTINCT roles so a
 * block's role fully encodes which writer behaviour produced it (the honest
 * Treatment ladder: a short take vs a deeper analysis). NEVER a user-facing label
 * on its own; it routes rendering and the per-section regenerate cue.
 */
export const CONTENT_BLOCK_ROLES = [
  'intro',
  'story',
  'commentary',
  'digest',
  'links',
  'signoff',
] as const;
export type ContentBlockRole = (typeof CONTENT_BLOCK_ROLES)[number];

/** Whether the owner has kept a block in the issue or set it aside. */
export const CONTENT_BLOCK_STATUSES = ['kept', 'dismissed'] as const;
export type ContentBlockStatus = (typeof CONTENT_BLOCK_STATUSES)[number];

/** One cited source under a block: a headline title and the URL a reader opens. */
export const contentSourceRefSchema = z.object({
  title: z.string().trim().min(1).max(300),
  url: z.string().trim().min(1).max(2000),
});
export type ContentSourceRef = z.infer<typeof contentSourceRefSchema>;

/**
 * One block of a persisted issue. `body` is prose (markdown-ish, inline links)
 * and DEFAULTS to `''` so a freshly assembled block that has no written prose yet
 * is valid (the P2 seam that fills it). `order` is a 0-based running position;
 * `status` defaults to `kept`. `sectionId` binds a block back to the discovered
 * section it was assembled from (absent for intro/signoff).
 */
export const contentBlockSchema = z.object({
  id: z.string().trim().min(1).max(120),
  role: z.enum(CONTENT_BLOCK_ROLES),
  title: z.string().trim().min(1).max(300).optional(),
  body: z.string().max(20000).default(''),
  sourceRefs: z.array(contentSourceRefSchema).max(50).optional(),
  sectionId: z.string().trim().min(1).max(120).optional(),
  order: z.number().int().min(0).max(1000),
  status: z.enum(CONTENT_BLOCK_STATUSES).default('kept'),
});
export type ContentBlock = z.infer<typeof contentBlockSchema>;

/**
 * An issue's lifecycle. `draft` = assembled and editable; `ready` = the finished
 * draft (the async generator persists a completed issue as `ready`); `generating`
 * = the issue is being (re)assembled off the request path by the generator Lambda,
 * so the studio polls until it flips to `ready`; `error` = a generation stopped on a
 * terminal condition (today: the monthly AI budget was reached) and the studio should
 * stop polling and show {@link Issue.statusReason}. Additive: older rows that predate a
 * value simply never carried it, and default reads still land on `draft`.
 */
export const ISSUE_STATUSES = ['draft', 'ready', 'generating', 'error'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

/** A persisted first issue: the current draft for one newsletter (not versioned in P1). */
export interface Issue {
  readonly tenantId: TenantId;
  readonly newsletterId: NewsletterId;
  readonly issueId: string;
  readonly subject: string;
  readonly blocks: readonly ContentBlock[];
  readonly status: IssueStatus;
  /**
   * A calm, user-facing explanation for a TERMINAL `error` status (e.g. "Our AI is
   * paused for this month because we reached this month's usage limit."). Absent for
   * every non-error status.
   */
  readonly statusReason?: string;
  /** When the blocks were last (re)assembled/written (ISO 8601). */
  readonly generatedAt: string;
  /** What wrote the blocks: `'assembly'` in P1, a model id (e.g. a Sonnet id) in P2. */
  readonly generatedBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * A lightweight "generation in progress" marker for a newsletter's draft issue,
 * persisted alongside (never overwriting) the draft row so a slow, off-request
 * generation is observable. `POST /issue/generate` (and the lazy first-open GET)
 * write one, enqueue a job carrying its `jobId`, and return `generating` fast; the
 * generator Lambda clears it (conditional on `jobId`, so a superseding regenerate's
 * marker is never cleared by an older run) once the finished issue is persisted.
 * `startedAt` lets a reader treat a marker older than the worker's whole budget as
 * stale and re-enqueue, so a crashed/timed-out generation self-heals rather than
 * pinning the studio on `generating` forever.
 */
export interface IssueGenerationState {
  readonly tenantId: TenantId;
  readonly newsletterId: NewsletterId;
  /** The generation job this marker was minted for; the supersede/clear guard. */
  readonly jobId: string;
  /** When this generation was enqueued (ISO 8601); drives the staleness backstop. */
  readonly startedAt: string;
  /**
   * The marker's state. Absent/`generating` = in flight (the studio polls); `error` =
   * the generation stopped on a TERMINAL condition (today: the monthly AI budget was
   * reached), so a reader surfaces it as a finished `error` issue and the studio stops
   * polling. Additive: markers written before this field simply never carried it.
   */
  readonly status?: 'generating' | 'error';
  /** For an `error` marker, the calm user-facing reason the generation stopped. */
  readonly reason?: string;
}

/**
 * One edit to a single block, matched by `id`. Every field is optional so a
 * caller can rename, rewrite, reorder, or keep/dismiss a block independently. A
 * blank `title` clears it; `body` is prose. Applied by PATCH /newsletters/{id}/issue.
 */
export const contentBlockEditSchema = z.object({
  id: z.string().trim().min(1).max(120),
  title: z.string().trim().max(300).optional(),
  body: z.string().max(20000).optional(),
  order: z.number().int().min(0).max(1000).optional(),
  status: z.enum(CONTENT_BLOCK_STATUSES).optional(),
  // The render role, re-derived from the section's treatment on a per-section
  // regenerate so a treatment change (e.g. "AI digest" -> "Curated list") actually
  // re-renders the block. Enum-validated; omitted on ordinary body/title edits.
  role: z.enum(CONTENT_BLOCK_ROLES).optional(),
});
export type ContentBlockEdit = z.infer<typeof contentBlockEditSchema>;

/**
 * Body accepted by PATCH /newsletters/{id}/issue: an optional new subject plus a
 * bounded batch of per-block edits. The full intended block set is sent each time
 * (last-write-wins), so a nested-list edit can never race a partial patch.
 *
 * `expectedIssueId` is the OPTIMISTIC-CONCURRENCY token: the id of the draft the
 * caller edited against. When present the server applies the patch ONLY while the
 * stored draft still carries that same issueId, so a late patch cannot clobber a
 * draft a regenerate replaced out from under it (a regenerate mints a NEW issueId).
 * A mismatch is a benign 409 the client is expected to ignore.
 */
export const issueEditSchema = z.object({
  subject: z.string().trim().min(1).max(300).optional(),
  blocks: z.array(contentBlockEditSchema).max(100).optional(),
  expectedIssueId: z.string().trim().min(1).max(120).optional(),
});
export type IssueEditInput = z.infer<typeof issueEditSchema>;

// --- Subscribers ------------------------------------------------------------
// A newsletter owns a list of subscribers. The owner can add, import, list, and
// remove subscribers, and the double opt-in flow (slice S3) drives them from
// `pending` to `confirmed` (on confirmation) or `unsubscribed` (on opt-out). A
// subscriber added or imported by the owner still starts `pending` and is sent a
// confirmation email; only a confirmed address is a real, opted-in recipient.

/**
 * A subscriber's lifecycle:
 *  - `pending`      - on the list but NOT double-opted-in yet (a confirmation
 *    email has been sent; they have not clicked it);
 *  - `confirmed`    - clicked the confirmation link (a real, opted-in recipient);
 *  - `unsubscribed` - opted out via an unsubscribe link (idempotent, terminal);
 *  - `suppressed`   - held back by the platform (e.g. a hard bounce or spam
 *    complaint from the S4 bounce pipeline); never emailed again.
 */
export const SUBSCRIBER_STATUSES = ['pending', 'confirmed', 'unsubscribed', 'suppressed'] as const;
export type SubscriberStatus = (typeof SUBSCRIBER_STATUSES)[number];

/** How a subscriber got onto the list: typed in one at a time, or bulk-imported. */
export const SUBSCRIBER_SOURCES = ['manual', 'import'] as const;
export type SubscriberSource = (typeof SUBSCRIBER_SOURCES)[number];

/**
 * Why the platform suppressed a subscriber (the terminal `suppressed` state the
 * S4 bounce/complaint pipeline drives):
 *  - `bounce`    - a hard (Permanent) bounce from the recipient's mail server;
 *  - `complaint` - the recipient marked our mail as spam.
 * Soft (Transient) bounces are deliberately NOT a reason: they never suppress.
 */
export const SUPPRESSED_REASONS = ['bounce', 'complaint'] as const;
export type SuppressedReason = (typeof SUPPRESSED_REASONS)[number];

/** The most emails accepted in a single bulk import, a guard against huge pastes. */
export const SUBSCRIBER_IMPORT_CAP = 5000;

/** One subscriber on a newsletter's list, scoped to its tenant + newsletter. */
export interface Subscriber {
  readonly tenantId: TenantId;
  readonly newsletterId: NewsletterId;
  readonly subscriberId: string;
  /** Always stored lowercased + trimmed so email-uniqueness is case-insensitive. */
  readonly email: string;
  readonly status: SubscriberStatus;
  /** How they were added; absent on rows that predate the field. */
  readonly source?: SubscriberSource;
  readonly createdAt: string;
  /** Set once a future double-opt-in confirms them; absent while `pending`. */
  readonly confirmedAt?: string;
  /**
   * Why the S4 pipeline suppressed this subscriber (a hard bounce or a spam
   * complaint); absent unless `status` is `suppressed`.
   */
  readonly suppressedReason?: SuppressedReason;
  /** When the S4 pipeline suppressed this subscriber; absent unless suppressed. */
  readonly suppressedAt?: string;
}

/**
 * One email, normalized (trimmed + lowercased) and validated. Lowercasing is what
 * makes the per-newsletter uniqueness guard case-insensitive: `A@x.com` and
 * `a@x.com` collapse to the same value before the guard item is keyed.
 */
export const subscriberEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.string().email('Enter a valid email address').max(200));

/** Body accepted by POST /newsletters/{id}/subscribers: a single email to add. */
export const subscriberInputSchema = z.object({
  email: subscriberEmailSchema,
});
export type SubscriberInput = z.infer<typeof subscriberInputSchema>;

/**
 * Body accepted by POST /newsletters/{id}/subscribers/import: the RAW pasted
 * lines. The array is capped (so a huge paste is refused early) and required to
 * be non-empty, but the lines are deliberately NOT normalized, validated, or
 * de-duplicated here: that categorization is done server-side by
 * {@link SubscriberImportResult} so every line can be counted into an honest
 * added / already-on-list / duplicate-in-paste / invalid breakdown that
 * reconciles with exactly what the owner pasted. Rejecting the whole batch on one
 * bad line would be hostile, so validation is tolerant and per-line downstream.
 */
export const subscriberImportSchema = z.object({
  emails: z.array(z.string()).min(1, 'Add at least one email').max(SUBSCRIBER_IMPORT_CAP),
});
export type SubscriberImportInput = z.infer<typeof subscriberImportSchema>;

/**
 * The full, reconcilable outcome of a bulk import. Every pasted line lands in
 * exactly one bucket (blank lines are ignored, not counted), so
 * `added + skippedExisting + skippedDuplicateInPaste + invalid` equals the number
 * of non-blank lines pasted. The server derives each bucket: `invalid` from
 * per-line email validation, `skippedDuplicateInPaste` from normalization,
 * `skippedExisting` from the conditional-write skip, and `added` from the writes
 * that landed. `invalidLines` echoes the rejected raw lines back so the UI can
 * show the owner exactly which entries were dropped.
 */
export interface SubscriberImportResult {
  readonly added: number;
  /** Emails skipped because they were already on the newsletter's list. */
  readonly skippedExisting: number;
  /** Valid emails skipped because the same address appeared earlier in the paste. */
  readonly skippedDuplicateInPaste: number;
  /** Lines rejected because they are not a valid email address. */
  readonly invalid: number;
  /** The rejected raw lines, echoed back so the UI can show what was dropped. */
  readonly invalidLines?: readonly string[];
}

/** What GET /newsletters/{id}/subscribers returns: the list plus its count. */
export interface SubscriberListResponse {
  readonly subscribers: readonly Subscriber[];
  readonly count: number;
}

// --- Sent-issue history -----------------------------------------------------
// Sending is LIVE and irreversible, so every real send-to-subscribers writes ONE
// durable, tenant-scoped history row at trigger time. It is a small record (the
// subject, when it went out, how many readers it was sent to, and its issue
// number), NOT the full frozen block content (that lives in the send marker). It
// gives the owner an honest, read-only record of what actually went out. A
// send-TEST never writes one.

/**
 * One record of a real issue send-to-subscribers. Written once per send trigger
 * (never per-recipient, never on a send-test), scoped to its tenant + newsletter.
 * `recipientCount` is the confirmed-subscriber count the fan-out was enqueued for
 * at trigger time (v1 does not reconcile a post-fan-out delivered number).
 */
export interface SentIssue {
  readonly tenantId: TenantId;
  readonly newsletterId: NewsletterId;
  /** The send trigger's id (one per real send), so a row maps back to its send. */
  readonly sendId: string;
  /** The subject line readers saw, frozen at trigger time. */
  readonly subject: string;
  /** When the send was triggered (UTC ISO 8601). */
  readonly sentAt: string;
  /** How many CONFIRMED readers the fan-out was enqueued for at trigger time. */
  readonly recipientCount: number;
  /** This issue's number (the newsletter's issuesSent + 1 at send time). */
  readonly issueNumber: number;
  /** The frozen draft's issueId this send rendered from, when known. */
  readonly issueId?: string;
}

/** What GET /newsletters/{id}/sent-issues returns: the history (newest first) + count. */
export interface SentIssueListResponse {
  readonly sentIssues: readonly SentIssue[];
  readonly count: number;
}

// --- Subscriber file upload -------------------------------------------------
// The owner can import a subscriber list by UPLOADING a CSV or plain-text file
// instead of pasting. Because the file carries PII and untrusted content, the
// upload never flows through our API: the client asks for a CONSTRAINED presigned
// S3 POST (POST-policy conditions pin an exact tenant-scoped key, a size cap, and
// an allowed Content-Type), uploads straight to a private bucket, then calls
// import-file with only an opaque `uploadId`. The server rebuilds the object key
// from the token's tenantId (never the client), reads the object under a hard byte
// cap, parses it safely (strict email validation, no formula/macro passthrough),
// runs the SAME `importMany` the paste importer uses, and deletes the object.

/**
 * The declared shape of an uploaded subscriber file. `csv` is parsed as RFC 4180
 * (quoted fields, embedded commas/newlines, CRLF); `text` is treated as one
 * candidate per line. Either way ONLY strict-email tokens are ever imported.
 */
export const UPLOAD_FILE_KINDS = ['csv', 'text'] as const;
export type UploadFileKind = (typeof UPLOAD_FILE_KINDS)[number];

/**
 * The hard ceiling on an uploaded file, enforced in THREE independent layers:
 * the presigned POST policy's `content-length-range`, a streaming byte cap when
 * the server reads the object back, and the bucket lifecycle backstop. 5 MB holds
 * far more addresses than the {@link SUBSCRIBER_IMPORT_CAP} we actually import.
 */
export const SUBSCRIBER_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

/**
 * The Content-Types a presigned upload is allowed to declare. Pinned as an EXACT
 * POST-policy condition so the browser cannot upload an arbitrary blob (e.g. an
 * executable) under a subscriber-file key. `application/vnd.ms-excel` is included
 * because spreadsheet tools commonly label an exported .csv with it. This is a
 * defense-in-depth hint only: the real safety is that the parser emits nothing
 * but values that pass strict email validation.
 */
export const SUBSCRIBER_UPLOAD_CONTENT_TYPES = [
  'text/csv',
  'text/plain',
  'application/vnd.ms-excel',
] as const;
export type SubscriberUploadContentType = (typeof SUBSCRIBER_UPLOAD_CONTENT_TYPES)[number];

/**
 * How long a presigned upload stays valid. Short by design: the URL is handed to
 * the browser and used immediately, so a stale/leaked URL expires fast.
 */
export const SUBSCRIBER_UPLOAD_EXPIRY_SECONDS = 300;

/**
 * Body accepted by POST /newsletters/{id}/subscribers/upload-url: the exact
 * Content-Type the browser will send, chosen from the allowed set. The server
 * pins it into the presigned POST policy so the eventual upload must match.
 */
export const subscriberUploadUrlSchema = z.object({
  contentType: z.enum(SUBSCRIBER_UPLOAD_CONTENT_TYPES),
});
export type SubscriberUploadUrlInput = z.infer<typeof subscriberUploadUrlSchema>;

/**
 * The constrained presigned POST returned to the browser. `url` is the S3 bucket
 * endpoint; `fields` are the exact form fields (including the server-pinned
 * `key`, `Content-Type`, and signed policy) that MUST be posted verbatim. The
 * client references the object afterwards only by the opaque `uploadId`; it never
 * needs, and is never separately handed, the full storage key.
 */
export interface SubscriberUploadTarget {
  readonly url: string;
  readonly fields: Record<string, string>;
}

/** What POST /newsletters/{id}/subscribers/upload-url returns. */
export interface SubscriberUploadUrlResponse {
  /** Opaque handle for the just-created upload slot; passed back to import-file. */
  readonly uploadId: string;
  /** The constrained presigned POST the browser uploads the file with. */
  readonly upload: SubscriberUploadTarget;
}

/**
 * The most characters an opaque `uploadId` may carry. It is a server-minted id
 * (`createId('upl')`), so this is a defensive bound on caller-supplied input.
 */
export const SUBSCRIBER_UPLOAD_ID_MAX = 120;

/**
 * Body accepted by POST /newsletters/{id}/subscribers/import-file. The `uploadId`
 * is the opaque handle from upload-url and is constrained to a safe id charset so
 * it can never be shaped into a key that escapes the caller's own prefix; `kind`
 * tells the parser how to read the bytes. No key, tenantId, or path is accepted:
 * the server rebuilds the object key from the token's tenant + this uploadId.
 */
export const subscriberImportFileSchema = z.object({
  uploadId: z
    .string()
    .trim()
    .min(1)
    .max(SUBSCRIBER_UPLOAD_ID_MAX)
    .regex(/^[A-Za-z0-9_-]+$/, 'Invalid upload id'),
  kind: z.enum(UPLOAD_FILE_KINDS),
});
export type SubscriberImportFileInput = z.infer<typeof subscriberImportFileSchema>;
